import { Inject, Injectable, Optional } from '@angular/core';
import {
  BindUserToAgent,
  BulkEmailSendingResponse,
  ChangeSourceType,
  ConfirmEmail,
  DeleteAgent,
  DeleteConference,
  DeleteConferenceRegistration,
  DeleteFirebaseUser,
  DeleteMediaPayload,
  DeleteWeekTransactions,
  EmailToSendConfig,
  FunctionsNames,
  GetFileUploadUrlPayload,
  GetFileUploadUrlResponse,
  GetImageContentByUrlPayload,
  GetImageContentByUrlResponse,
  PaymentIntentPayload,
  ProcessPaymentPayload,
  ProcessRefundPayload,
  RegistrationSummaryEmail,
  SearchRequest,
  SearchResponse,
  SendOTP,
  SignUpRequest,
  SignUpResponse,
  SupportTaskEmailToAgent,
  UpdateAgentGoalsPayload,
  UpdateUserLoginEmail,
  UpdateUserPassword,
  UploadFilePayload,
  UploadMediaPayload,
  ValidateOtp,
  CreateTempUploadSessionPayload,
  CreateTempUploadSessionResponse,
} from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { AppCheck, getToken as getAppCheckToken } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { FIREBASE_APP } from '../injections/firebase-app';

import saveAs from 'file-saver';
import { USE_CLOUD_FUNCTIONS_EMULATOR } from '../injections/use-cloud-functions-emulator';
import {
  GetStatsPayload,
  GetStatsResponse,
  PushNotificationCampaign,
} from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib';
import { CopyConferenceRegistration } from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/functions/copy-conference-registration.model';
import { ParsedEmail } from '../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/email-parser.model';
import { BaseCloudFunctionsService } from '../utils/base-cloud-functions-service';
import { RegistrationParticipationEmail } from '@ag-common-lib/lib/models/functions/registration-participation-email.model';
import { ShipDocumentToElasticPayload } from '@ag-common-lib/lib/models/functions/ship-document-to-elastic.model';
import { FormBuilderAction } from '@ag-common-lib/lib/models/email/form-builder-action.model';

interface HttpFunctionRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: unknown;
  body?: unknown;
  headers?: Record<string, string>;
  responseType?: 'auto' | 'json' | 'text' | 'blob';
}

@Injectable({
  providedIn: 'root',
})
export class CloudFunctionsService extends BaseCloudFunctionsService {
  private readonly firebaseAppInstance: FirebaseApp;

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    @Optional() @Inject(USE_CLOUD_FUNCTIONS_EMULATOR) useEmulator = false,
  ) {
    super(fireBaseApp, useEmulator);
    this.firebaseAppInstance = fireBaseApp;
  }

  requestHttpFunction = <T>(functionName: FunctionsNames, options: HttpFunctionRequestOptions = {}): Promise<T> => {
    return this.sendHttpFunction<T>(functionName, options);
  };

  requestHttpFunctionPath = <T>(
    functionName: FunctionsNames,
    path: string,
    options: HttpFunctionRequestOptions = {},
  ): Promise<T> => {
    return this.sendHttpFunction<T>(functionName, options, path);
  };

  sendOTP = (payload: SendOTP): Promise<any> => httpsCallable(this.functions, FunctionsNames.sendOTP)(payload);

  signUp = (payload: SignUpRequest): Promise<SignUpResponse> =>
    httpsCallable<SignUpRequest, SignUpResponse>(this.functions, FunctionsNames.signUp)(payload).then(
      response => response?.data,
    );

  checkLoginEmail = (email: string): Promise<any> =>
    httpsCallable(this.functions, FunctionsNames.checkLoginEmail)({ email });

  bindUserToAgent = (uid: string, agentDbId: string): Promise<any> => {
    const payload: BindUserToAgent = { uid, agentDbId };

    return httpsCallable(this.functions, FunctionsNames.bindUserToAgent)(payload);
  };

  confirmEmail = (payload: ConfirmEmail): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.confirmEmail)(payload);
  };

  deleteAgent = (agentDbId: string, actionName: ChangeSourceType | null = null): Promise<any> => {
    const payload: DeleteAgent = { agentDbId, actionName };

    return httpsCallable(this.functions, FunctionsNames.deleteAgent)(payload);
  };

  deleteFirebaseUser = (uid: string): Promise<any> => {
    const payload: DeleteFirebaseUser = { uid };

    return httpsCallable(this.functions, FunctionsNames.deleteFirebaseUser)(payload);
  };

  findAgentDbIdByLoginEmail = (payload: { email }): Promise<string> =>
    httpsCallable<{ email: string }, string>(
      this.functions,
      FunctionsNames.findAgentDbIdByLoginEmail,
    )(payload).then(response => response?.data);

  updateUserLoginEmail = (payload: UpdateUserLoginEmail): Promise<any> =>
    httpsCallable(this.functions, FunctionsNames.updateUserLoginEmail)(payload);

  forceChangeUserLoginEmail = (payload: UpdateUserLoginEmail): Promise<any> =>
    httpsCallable(this.functions, FunctionsNames.forceChangeUserLoginEmail)(payload);

  updateSalesGoals = (payload: UpdateAgentGoalsPayload[]): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.updateSalesGoals)(payload);
  };

  validateOTP = (payload: ValidateOtp): Promise<{ isValid: boolean; reason?: string }> => {
    return httpsCallable(this.functions, FunctionsNames.validateOTP)(payload).then(
      response => response?.data as { isValid: boolean; reason?: string },
    );
  };

  collectPaymentIntent = (payload: PaymentIntentPayload): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.collectPaymentIntent)(payload);
  };

  processPayment = (payload: ProcessPaymentPayload): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.processPayment)(payload);
  };

  refundPayment = (payload: ProcessRefundPayload): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.refundPayment)(payload);
  };

  handleWebhookPayment = (): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.handleWebhookPayment)();
  };

  getPaymentEventListByTypes = (types: string[]): Promise<any> => {
    console.log(types);
    return httpsCallable(this.functions, FunctionsNames.getPaymentEventListByTypes)({ types });
  };

  updateUserPassword = (payload: UpdateUserPassword): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.updateUserPassword)(payload);
  };

  searchWithElastic = (payload: SearchRequest): Promise<HttpsCallableResult<SearchResponse>> => {
    return this.requestHttpFunction<{ status: number; data: SearchResponse }>(FunctionsNames.elasticSearchGen2, {
      method: 'POST',
      body: {
        data: payload,
      },
      responseType: 'json',
    }).then(response => ({ data: response?.data }) as HttpsCallableResult<SearchResponse>);
  };

  uploadMedia = (payload: UploadMediaPayload): Promise<string> => {
    return httpsCallable<UploadMediaPayload, string>(
      this.functions,
      FunctionsNames.uploadMedia,
    )(payload).then(res => {
      return res?.data;
    });
  };

  createTempUploadSession = (payload: CreateTempUploadSessionPayload): Promise<CreateTempUploadSessionResponse> => {
    return httpsCallable<CreateTempUploadSessionPayload, CreateTempUploadSessionResponse>(
      this.functions,
      FunctionsNames.createTempUploadSession,
    )(payload).then(res => res?.data);
  };

  deleteMedia = (payload: DeleteMediaPayload): Promise<string> => {
    return httpsCallable<DeleteMediaPayload, string>(
      this.functions,
      FunctionsNames.deleteMedia,
    )(payload).then(res => {
      return res?.data;
    });
  };

  getImageContentByUrl = (payload: GetImageContentByUrlPayload): Promise<GetImageContentByUrlResponse> => {
    return httpsCallable<GetImageContentByUrlPayload, GetImageContentByUrlResponse>(
      this.functions,
      FunctionsNames.getImageContentByUrl,
    )(payload).then(response => response?.data);
  };

  deleteConferenceRegistration = (payload: DeleteConferenceRegistration): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.deleteConferenceRegistration)(payload);
  };

  copyConferenceRegistration = (payload: CopyConferenceRegistration): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.copyConferenceRegistration)(payload);
  };

  sendConferenceRegistrationSummary = (
    payload: RegistrationSummaryEmail | RegistrationParticipationEmail,
  ): Promise<any> => httpsCallable(this.functions, FunctionsNames.registrationSummaryEmail)(payload);

  bulkEmailSending = (payload: EmailToSendConfig[]): Promise<HttpsCallableResult<BulkEmailSendingResponse>> =>
    httpsCallable<EmailToSendConfig[], BulkEmailSendingResponse>(
      this.functions,
      FunctionsNames.bulkEmailSending,
    )(payload);

  sendSupportTaskEmailToAgent = (urlToSupportTask: string, inviteeEmail: string, fullName: string): Promise<any> => {
    const payload: SupportTaskEmailToAgent = { urlToSupportTask, inviteeEmail, fullName };
    return httpsCallable(this.functions, FunctionsNames.supportTaskEmailToAgent)(payload);
  };

  getArchivedImages = async (media: any): Promise<void> => {
    await fetch(this.getFunctionUrl(FunctionsNames.getArchivedImages), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          media,
          optimized: true,
        },
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob(); // Get the response as a Blob object
      })
      .then(blob => {
        // Handle the response
        saveAs(blob, 'images.zip');
      })
      .catch(error => {
        // Handle errors
        console.error('Error calling function:', error);
      });
  };

  getAuthentication = async (): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.getAuthentication)();
  };

  deleteAuthentication = async (uid: string, action: ChangeSourceType | null = null): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.deleteAuthentication)({ uid, action });
  };

  forceVerifyEmailAuthentication = async (uid: string): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.forceVerifyEmailAuthentication)({ uid });
  };

  sendPushNotification = (payload: PushNotificationCampaign): Promise<any> =>
    httpsCallable(this.functions, FunctionsNames.sendPushNotification)(payload);

  createCustomToken = (): Promise<HttpsCallableResult<{ token: string }>> =>
    httpsCallable<void, { token: string }>(this.functions, FunctionsNames.createCustomToken)();

  getStats = (payload: GetStatsPayload): Promise<HttpsCallableResult<GetStatsResponse>> =>
    httpsCallable<GetStatsPayload, GetStatsResponse>(this.functions, FunctionsNames.getStats)(payload);

  uploadFile = (payload: UploadFilePayload): Promise<string> => {
    return httpsCallable<UploadFilePayload, string>(
      this.functions,
      FunctionsNames.uploadFile,
    )(payload).then(res => {
      return res?.data;
    });
  };

  getFileUploadUrl = (payload: GetFileUploadUrlPayload): Promise<GetFileUploadUrlResponse> => {
    return httpsCallable<GetFileUploadUrlPayload, GetFileUploadUrlResponse>(
      this.functions,
      FunctionsNames.getFileUploadUrl,
    )(payload).then(res => {
      return res?.data;
    });
  };

  deleteWeekTransactions = async (payload: DeleteWeekTransactions): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.deleteWeekTransactions)(payload);
  };

  /*Payload type is base64 string*/
  parseMSGFile = async (payload: string): Promise<ParsedEmail> => {
    return httpsCallable<string, ParsedEmail>(
      this.functions,
      FunctionsNames.parseMSGFile,
    )(payload).then(response => response?.data);
  };

  sendEmailTemplate = (payload: Record<string, unknown>): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.sendEmailTemplate)(payload).then(response => response?.data);
  };

  deleteConference = (payload: DeleteConference): Promise<any> => {
    return httpsCallable(this.functions, FunctionsNames.deleteConference)(payload);
  };

  shipDocumentToElastic = (payload: ShipDocumentToElasticPayload): Promise<void> => {
    return httpsCallable(this.functions, FunctionsNames.shipDocumentToElastic)(payload).then();
  };

  createFormBuilderActionPayload = (payload: FormBuilderAction): Promise<void> => {
    return httpsCallable(this.functions, FunctionsNames.createFormBuilderActionPayload)(payload).then();
  };

  private async sendHttpFunction<T>(
    functionName: FunctionsNames,
    options: HttpFunctionRequestOptions,
    path = '',
  ): Promise<T> {
    const method = options.method ?? 'POST';
    const responseType = options.responseType ?? 'auto';
    const hasBody = options.body !== undefined && options.body !== null;
    const headers: Record<string, string> = {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(await this.getSecurityHeaders()),
      ...(options.headers ?? {}),
    };
    const response = await fetch(this.getHttpFunctionUrl(functionName, options.query, path), {
      method,
      headers,
      body: hasBody ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const details = await response.text();
      throw new Error(`Cloud Function request failed (${response.status}): ${details || response.statusText}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (responseType === 'blob') {
      return (await response.blob()) as T;
    }

    if (responseType === 'json') {
      return (await response.json()) as T;
    }

    if (responseType === 'text') {
      return (await response.text()) as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }

    return (await response.text()) as T;
  }

  private getHttpFunctionUrl(functionName: FunctionsNames, query?: unknown, path = ''): string {
    const normalizedPath = path ? `/${String(path).replace(/^\/+/, '')}` : '';
    const baseUrl = `${this.getFunctionUrl(functionName)}${normalizedPath}`;

    if (!query || typeof query !== 'object') {
      return baseUrl;
    }

    const params = new URLSearchParams();
    Object.entries(query as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        params.set(key, String(value));
        return;
      }

      params.set(key, JSON.stringify(value));
    });

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  private async getSecurityHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};
    const authToken = await this.getAuthToken();
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const appCheckToken = await this.getAppCheckToken();
    if (appCheckToken) {
      headers['X-Firebase-AppCheck'] = appCheckToken;
    }

    return headers;
  }

  private async getAuthToken(): Promise<string | null> {
    const auth = getAuth(this.firebaseAppInstance);
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return null;
    }

    return currentUser.getIdToken();
  }

  private async getAppCheckToken(): Promise<string | null> {
    try {
      const appCheck = this.getInitializedAppCheck();
      if (!appCheck) {
        return null;
      }

      const token = await getAppCheckToken(appCheck, false);
      return token.token ?? null;
    } catch {
      return null;
    }
  }

  private getInitializedAppCheck(): AppCheck | null {
    const appCheckProvider = (this.firebaseAppInstance as any)?._container?.getProvider?.('app-check');
    return appCheckProvider?.getImmediate?.({ optional: true }) ?? null;
  }
}
